/**
 * AnnouncementBanner — the planned-outage strip, plus the modal for the rare
 * notice that warrants interrupting someone.
 *
 * Mounted once under the nav bar so it sits above every route.
 *
 * WHY THE BANNER IS THE PRIMARY SURFACE, not the modal.
 * A modal fires when someone OPENS the app — which is before they've hit
 * anything, and at the exact moment they're trying to get to work, so it gets
 * dismissed reflexively. The banner is what's on screen at 4:55pm when the
 * window starts at 5:00 and a landman is halfway through a deal form. That's
 * the moment the message has to land, and its real content isn't "we're
 * sorry", it's "save now, don't start another one".
 *
 * So: banner always; modal only for `require_ack`, which admins are told to
 * use sparingly. An app that throws a modal every week teaches people to
 * dismiss it unread, which destroys the one thing it's for.
 *
 * ESCALATION. The banner restyles itself as the window approaches (see
 * `announcementPhase`), and a dismissal only silences the phase it was made
 * in — so waving it away on Tuesday doesn't also silence it on Saturday.
 *
 * One banner at a time, most urgent first. Two stacked banners is a wall
 * people scroll past, and the second-most-urgent outage notice has never been
 * the one that mattered.
 */
import { useMemo } from "react";
import { Alert, Button, Modal, Space, Typography } from "antd";
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ToolOutlined,
} from "@ant-design/icons";

import useAnnouncements from "../hooks/useAnnouncements";
import {
  announcementPhase,
  describeTiming,
  formatClock,
  mostUrgent,
  shouldShowModal,
} from "../utils/announcementPhase";

const { Text, Paragraph } = Typography;

// Kinds that are informational by nature and must NEVER escalate their
// colour. A notice with no window sits permanently in the `active` phase, so
// without this an `info` notice renders as a red ERROR banner -- the same
// styling as "everything is down".
const INFORMATIONAL_KINDS = new Set(["info"]);

// For operational notices phase drives the colour more than severity does: an
// outage that is HAPPENING is red whatever its author expected, and one a week
// away is informational even if it is going to be painful.
const bannerType = (phase, severity, kind) => {
  if (INFORMATIONAL_KINDS.has(kind)) {
    return severity === "critical" ? "warning" : "info";
  }
  if (phase === "active") return "error";
  if (phase === "imminent") return "warning";
  return severity === "critical" ? "warning" : "info";
};

const KIND_ICON = {
  maintenance: <ToolOutlined />,
  outage: <ExclamationCircleOutlined />,
  info: <InfoCircleOutlined />,
};

/**
 * The window in the reader's own local zone, spelled out.
 *
 * Always local: an admin writing "8pm" in Oklahoma and a broker reading it
 * elsewhere must not be looking at the same string and picturing different
 * hours.
 */
const windowText = (a) => {
  if (!a?.starts_at) return null;
  const start = formatClock(a.starts_at);
  const end = a.ends_at ? formatClock(a.ends_at) : null;
  return end ? `${start} — ${end}` : `From ${start}`;
};

const AnnouncementBanner = () => {
  const { announcements, now, dismiss, acknowledge } = useAnnouncements();

  const banner = useMemo(
    () => mostUrgent(announcements, now),
    [announcements, now]
  );

  // The modal is chosen independently of the banner: an acknowledged modal
  // still leaves its banner up, and a dismissed banner doesn't cancel a modal
  // the user hasn't seen.
  const modalFor = useMemo(
    () => (announcements || []).find((a) => shouldShowModal(a, now)) || null,
    [announcements, now]
  );

  const bannerPhase = banner ? announcementPhase(banner, now) : null;
  const timing = banner ? describeTiming(banner, now) : null;
  const window = banner ? windowText(banner) : null;

  return (
    <>
      {banner && (
        <Alert
          banner
          showIcon
          icon={KIND_ICON[banner.kind] || KIND_ICON.maintenance}
          type={bannerType(bannerPhase, banner.severity, banner.kind)}
          closable
          onClose={() => dismiss(banner, bannerPhase)}
          title={
            <Space size={8} wrap>
              <Text strong>{banner.title}</Text>
              {timing && <Text type="secondary">· {timing}</Text>}
            </Space>
          }
          description={
            banner.body || window ? (
              <Space direction="vertical" size={0}>
                {banner.body && (
                  <span style={{ whiteSpace: "pre-wrap" }}>{banner.body}</span>
                )}
                {window && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <ClockCircleOutlined /> {window}
                  </Text>
                )}
              </Space>
            ) : null
          }
        />
      )}

      <Modal
        open={Boolean(modalFor)}
        title={modalFor?.title}
        // No X and no backdrop click: this is the surface an admin reached for
        // precisely because it must be read, so the only way out is the
        // button that records the acknowledgement.
        closable={false}
        mask={{ closable: false }}
        keyboard={false}
        // antd 6 spelling of destroyOnClose. Without it the acknowledged
        // modal's footer stays in the DOM, so a screen reader can still reach
        // a "Got it" button for a notice the user has already dealt with.
        destroyOnHidden
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => modalFor && acknowledge(modalFor)}
          >
            Got it
          </Button>,
        ]}
      >
        {modalFor && (
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {modalFor.body && (
              <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                {modalFor.body}
              </Paragraph>
            )}
            {windowText(modalFor) && (
              <Text strong>
                <ClockCircleOutlined /> {windowText(modalFor)}
              </Text>
            )}
            {describeTiming(modalFor, now) && (
              <Text type="secondary">{describeTiming(modalFor, now)}</Text>
            )}
          </Space>
        )}
      </Modal>
    </>
  );
};

export default AnnouncementBanner;
